import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { monthLabel } from "../lib/format";
import { Panel } from "./Widgets";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

export function ReassignPanel({ merchant, onDone }) {
  const [agents, setAgents] = useState([]);
  const [house, setHouse] = useState([]);
  const months = [...new Set(merchant.history.map((h) => h.month))].sort().reverse();
  const [target, setTarget] = useState("");
  const [from, setFrom] = useState(months[0] || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/agents").then((r) => setAgents(r.data));
    api.get("/settings").then((r) => setHouse(r.data.house_codes));
  }, []);
  useEffect(() => { setTarget(""); setFrom(months[0] || ""); }, [merchant.merchant_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    const label = target === "sheet" ? "the agent code in the sheet" : target.startsWith("house:") ? `House (${target.slice(6)})` : agents.find((a) => a.id === target)?.name;
    if (!window.confirm(`Move ${merchant.merchant_name || merchant.merchant_id} to ${label} from ${monthLabel(from)} onward? Payouts will be recalculated.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/merchants/${merchant.merchant_id}/reassign`, { target, from_month: from });
      toast.success(`Moved to ${data.to_owner}`);
      setTarget("");
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const ov = merchant.override;
  return (
    <Panel title="Reassign merchant" action={<ArrowRightLeft size={15} className="text-slate-500" />}>
      <div className="p-5 space-y-4" data-testid="reassign-panel">
        <div className="text-sm text-slate-400" data-testid="reassign-current">
          {ov ? <>Manually assigned to <span className="text-blue-300">{merchant.owner}</span> from <span className="font-mono text-slate-200">{monthLabel(ov.from_month)}</span> — future uploads follow this, not column M.</>
            : <>Follows the agent code in the sheet (column M: <span className="font-mono text-slate-200">{merchant.agent_code}</span>).</>}
        </div>
        <div className="grid sm:grid-cols-[1fr_180px_auto] gap-3 items-end">
          <div><div className="text-xs text-slate-400 mb-1.5">Move to</div>
            <Select value={target} onValueChange={setTarget}>
              <SelectTrigger data-testid="reassign-target-select" className="bg-[#0B0F17]"><SelectValue placeholder="Choose owner…" /></SelectTrigger>
              <SelectContent>
                {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.agent_code})</SelectItem>)}
                {house.map((h) => <SelectItem key={h} value={`house:${h}`}>House ({h})</SelectItem>)}
                {ov && <SelectItem value="sheet">Follow sheet code again</SelectItem>}
              </SelectContent>
            </Select></div>
          <div><div className="text-xs text-slate-400 mb-1.5">Starting month</div>
            <Select value={from} onValueChange={setFrom}>
              <SelectTrigger data-testid="reassign-month-select" className="bg-[#0B0F17] font-mono"><SelectValue /></SelectTrigger>
              <SelectContent>{months.map((m) => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}</SelectContent>
            </Select></div>
          <Button data-testid="reassign-submit-button" disabled={!target || !from || busy} onClick={submit} className="bg-blue-500 hover:bg-blue-600 text-white">{busy ? "Moving…" : "Reassign"}</Button>
        </div>
        {merchant.changes?.length > 0 && (
          <div className="border-t border-slate-800 pt-3 space-y-1.5" data-testid="reassign-log">
            {merchant.changes.map((c) => (
              <div key={c.id} className="text-xs flex justify-between gap-3">
                <span><span className="text-rose-300">{c.from_owner}</span> → <span className="text-emerald-400">{c.to_owner}</span> <span className="text-slate-500">from {monthLabel(c.from_month)}</span></span>
                <span className="font-mono text-slate-500">{new Date(c.at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
}
